import { WalletOutlined } from '@ant-design/icons';
import { App, Button } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import authService from '../services/authService';

const { useApp } = App;

interface PhantomWalletButtonProps {
  onSuccess: (token: string) => void;
}

interface PhantomProvider {
  isPhantom?: boolean;
  connect: (options?: { onlyIfTrusted?: boolean }) => Promise<{ publicKey: { toString: () => string } }>;
  disconnect: () => Promise<void>;
  signMessage: (message: Uint8Array, encoding: string) => Promise<{ signature: Uint8Array }>;
  publicKey?: { toString: () => string };
  isConnected?: boolean;
  on?: (event: string, callback: (args: any) => void) => void;
}

declare global {
  interface Window {
    solana?: PhantomProvider;
    phantom?: {
      solana?: PhantomProvider;
    };
  }
}

const PhantomWalletButton: React.FC<PhantomWalletButtonProps> = ({ onSuccess }) => {
  const { message } = useApp();
  const [loading, setLoading] = useState(false);
  const [hasPhantom, setHasPhantom] = useState(true); // Start optimistic (bright by default)
  const isProcessingRef = useRef(false);

  useEffect(() => {
    // Check if Phantom is installed - try both window.solana and window.phantom.solana
    const phantomProvider = window.solana?.isPhantom ? window.solana : window.phantom?.solana;

    // Only set to false if we confirm it's NOT installed
    if (!phantomProvider?.isPhantom) {
      setHasPhantom(false);
    }
  }, []);

  const handleWalletLogin = useCallback(async () => {
    // Prevent multiple simultaneous requests
    if (isProcessingRef.current || loading) return;

    // Get Phantom provider - try both window.solana and window.phantom.solana
    const provider = window.solana?.isPhantom ? window.solana : window.phantom?.solana;

    if (!provider || !provider.isPhantom) {
      message.error('Phantom wallet is not installed. Please install it from phantom.app');
      window.open('https://phantom.app/', '_blank');
      return;
    }

    isProcessingRef.current = true;
    setLoading(true);

    try {
      let walletAddress: string;

      // Check if already connected
      if (provider.isConnected && provider.publicKey) {
        walletAddress = provider.publicKey.toString();
      } else {

        // Show instructions to user
        message.warning({
          content: 'Please unlock your Phantom wallet and click the extension icon if no popup appears. Then try connecting again.',
          duration: 8,
        });

        // Check if wallet might be locked
        if (!provider.publicKey && !provider.isConnected) {
          message.info({
            content: 'Please unlock your Phantom wallet first, then click the button again.',
            duration: 5,
          });
        }

        try {
          // Check if connect is a function
          if (typeof provider.connect !== 'function') {
            throw new Error('Phantom wallet connect method is not available. Please ensure Phantom wallet is properly installed and enabled.');
          }

          // Use a shorter timeout to detect issues faster (10 seconds)
          // Then show manual connection instructions
          const connectPromise = provider.connect({ onlyIfTrusted: false });
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('timeout')), 10000)
          );

          const connectResponse = await Promise.race([connectPromise, timeoutPromise]) as { publicKey: { toString: () => string } };
          walletAddress = connectResponse.publicKey.toString();
          message.success('Successfully connected to Phantom wallet!');
        } catch (connectError: any) {
          // If it's a timeout, provide manual connection instructions
          if (connectError.message === 'timeout' || connectError.message?.includes('timeout')) {
            // Show manual connection instructions
            message.error({
              content: (
                <div>
                  <p><strong>Connection timeout</strong></p>
                  <p>Please try this:</p>
                  <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    <li>Click the <strong>Phantom extension icon</strong> in your browser toolbar</li>
                    <li>Look for a connection request and approve it</li>
                    <li>Make sure Phantom wallet is <strong>unlocked</strong></li>
                    <li>Check if popups are blocked for this site</li>
                  </ol>
                  <p>Then click the button again.</p>
                </div>
              ),
              duration: 15,
            });

            throw new Error('Connection timeout. Please follow the instructions shown and try again.');
          }

          // Handle other errors
          if (connectError.code === 4001) {
            throw new Error('Connection was rejected. Please try again and approve the connection.');
          }

          throw connectError;
        }
      }

      // Generate message for signing
      const authMessage = authService.generateAuthMessage(walletAddress);

      // Encode message as Uint8Array
      const encodedMessage = new TextEncoder().encode(authMessage);

      // Request signature from wallet - Phantom's signMessage API
      const signResponse = await provider.signMessage(encodedMessage, 'utf8');
      const signature = signResponse.signature;

      // Convert signature to base64 string (browser-compatible)
      const signatureBase64 = btoa(String.fromCharCode.apply(null, Array.from(signature)));

      // Send to backend for verification
      const token = await authService.loginWithWallet(
        walletAddress,
        signatureBase64,
        authMessage
      );

      onSuccess(token);
    } catch (error: any) {
      if (error.code === 4001 || error.message?.includes('reject') || error.message?.includes('User rejected')) {
        message.error('Wallet connection was rejected. Please try again and approve the connection.');
      } else if (error.message?.includes('User closed') || error.message?.includes('closed')) {
        message.error('Wallet popup was closed. Please try again.');
      } else if (error.message?.includes('timeout')) {
        message.error({
          content: error.message || 'Connection timeout. Try clicking the Phantom extension icon manually, or check if popups are blocked.',
          duration: 10,
        });
      } else {
        message.error(error.message || 'Wallet authentication failed. Please try again.');
      }
    } finally {
      setLoading(false);
      isProcessingRef.current = false;
    }
  }, [loading, onSuccess]);

  return (
    <Button
      icon={<WalletOutlined />}
      onClick={handleWalletLogin}
      loading={loading}
      block
      size="large"
      style={{
        height: 48,
        background: hasPhantom ? '#7B61FF' : '#666',
        borderColor: hasPhantom ? '#7B61FF' : '#666',
        color: 'white',
      }}
    >
      {hasPhantom ? 'Sign in with Phantom' : 'Install Phantom Wallet'}
    </Button>
  );
};

export default PhantomWalletButton;
